import { useMemo } from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';
import { Title } from '@restworld/ui-ds';
import type { Note as TNote } from '@restworld/utility-types';
import { formatDate } from '@restworld/utils-common';

import { CandidateNotesEditDeleteAction } from './candidate-notes-edit-delete-action';

const NoteContainer = styled.div`
  ${tw`border-t border-neutral-40 py-3 w-full`}
`;

const NoteHeader = styled.div`
  ${tw`text-neutral-700 flex justify-between items-center mb-3`}
`;

const UserInfo = styled.div`
  ${tw`flex items-center gap-3`}
`;

const UserAvatar = styled.div`
  ${tw`h-8 w-8 rounded-full border border-purple-700 font-semibold text-purple-700 grid place-items-center bg-purple-50`}
`;

const UserName = styled(Title)`
  ${tw`text-neutral-700`}
`;

const NoteDate = styled.span`
  ${tw`text-neutral-100 font-medium`}
`;

const NoteContent = styled(Title)`
  ${tw`w-fit truncate`}
`;

export const CandidateNoteItem = ({
  last_modified_by_user: lastModifiedByUser,
  inserted_at: insertedAt,
  note,
  id,
}: TNote) => {
  const { t } = useTranslation('job-positions-page');
  const name = lastModifiedByUser?.name ?? '';
  const surname = lastModifiedByUser?.surname ?? '';
  const userId = lastModifiedByUser?.id;
  const { user } = useAuth();
  const title = useMemo(
    () => (userId === user?.id ? t('you') : `${name ?? '-'} ${surname ?? ''}`),
    [userId, user?.id, t, name, surname]
  );
  return (
    <NoteContainer id={id?.toString()}>
      <NoteHeader>
        <UserInfo>
          <UserAvatar>
            <span>
              {name?.split('')[0] ?? 'N'}
              {surname?.split('')[0] ?? 'A'}
            </span>
          </UserAvatar>
          <div>
            <UserName fontSize="base" fontWeight="semibold">
              {title}
            </UserName>
            <NoteDate>{formatDate(insertedAt)}</NoteDate>
          </div>
        </UserInfo>
        {userId === user?.id && <CandidateNotesEditDeleteAction noteId={id} note={note} />}
      </NoteHeader>
      <NoteContent content={note} textColor="neutral-700" />
    </NoteContainer>
  );
};
